import { useMemo } from 'react';

import { FilterType } from '@work4all/components';
import { CheckboxCell } from '@work4all/components/lib/dataDisplay/basic-table/utils/cells/CheckboxCell';

import { AppParts, useCanView, useUser } from '@work4all/data';

import { TravelExpenses } from '@work4all/models/lib/Classes/TravelExpenses.entity';
import { SortDirection } from '@work4all/models/lib/DataProvider';

import { RelativeDateFilter } from '@work4all/utils/lib/date-utils/RelativeDateFilter.enum';

import { EntityTableV8, IEntityTableV8 } from './entity-table/EntityTableV8';
import { useEntityDataTableV8 } from './entity-table/use-entity-data-table-v8';
import schema from './schemata/travelExpenses-table-schema.json';

export type ITravelExpensesStatusCellConfig = {
  type: 'TravelExpensesStatus';
};

export type ITravelExpensesOpenStatusCellConfig = {
  type: 'TravelExpensesOpenStatus';
};

type ITravelExpensesTableCustomColumns = {
  TravelExpensesOpenStatus?: ITravelExpensesOpenStatusCellConfig;
};

const forceRequestFields: TravelExpenses = {
  closedByuserid: null,
  insertionREDate: null,
  approved: null,
  closeDate: null,
  businessTripContractNumber: null,
};

const defaultSort = [{ field: 'date', direction: SortDirection.DESCENDING }];

// Custom open status cell component for travel expenses
const TravelExpensesOpenStatusCell = (props: {
  row: { original: TravelExpenses };
}) => {
  const travelExpenses = props.row.original;

  // Show checked when closedByuserid is null (Open), unchecked when it's set (Closed)
  const isOpen = !travelExpenses.closedByuserid;

  return <CheckboxCell value={isOpen} />;
};

export function TravelExpensesTable(props: IEntityTableV8) {
  const prefilter = useOwnTravelExpenses();

  const dataTable = useEntityDataTableV8<
    TravelExpenses,
    ITravelExpensesTableCustomColumns
  >({
    ...props,
    prefilter,
    schema: schema as never,
    defaultSort,
    forceRequestFields,
    defaultColumnFilters: [
      {
        id: 'date',
        value: {
          value: {
            startDate: RelativeDateFilter.currentYear,
          },
          filterType: FilterType.Date,
        },
      },
    ],
    cells: {
      TravelExpensesOpenStatus: TravelExpensesOpenStatusCell,
    },
    rowModifiers: (value) => {
      const isApproved = value.approved === 1;
      const isClosed = !!value.closedByuserid;
      const isBooked = !!value.insertionREDate;

      return {
        isFaded: isBooked,
        isShade1: isClosed || isBooked,
        isSuccess: isApproved && !isClosed,
      };
    },
  });

  return (
    <EntityTableV8
      {...props}
      {...dataTable}
      areas={{
        right: null,
      }}
    />
  );
}

export const useOwnTravelExpenses = () => {
  const user = useUser();
  const prefilter = useMemo(() => {
    return {
      'user.id': {
        $eq: user.benutzerCode,
      },
    };
  }, [user.benutzerCode]);

  const canViewTravelExpenses = useCanView(AppParts.TRAVEL_RECEIPTS);
  const isFullUser = useCanView(AppParts.FULL);

  return canViewTravelExpenses && !isFullUser ? [prefilter] : [];
};
